/**
 * Per-resource "is the shared chat rail open" preference, persisted in
 * localStorage so each property/project remembers its own state. Centralized
 * here so the detail pages and the mention-toast click (ZPL-147) agree on the
 * exact keys — they must not drift.
 */

function railKey(resourceKind: "property" | "project", id: string): string {
	return resourceKind === "property"
		? `zplanem.chat-rail.${id}`
		: `zplanem.project-chat-rail.${id}`
}

function read(key: string): boolean {
	if (typeof window === "undefined") return false
	try {
		return window.localStorage.getItem(key) === "open"
	} catch {
		return false
	}
}

function write(key: string, open: boolean): void {
	if (typeof window === "undefined") return
	try {
		window.localStorage.setItem(key, open ? "open" : "closed")
	} catch {
		// localStorage can throw (private mode / quota); the rail just won't persist.
	}
}

export function readPropertyChatRailOpen(
	propertyId: string | undefined,
): boolean {
	return propertyId ? read(railKey("property", propertyId)) : false
}

export function writePropertyChatRailOpen(
	propertyId: string,
	open: boolean,
): void {
	write(railKey("property", propertyId), open)
}

export function readProjectChatRailOpen(
	projectId: string | undefined,
): boolean {
	return projectId ? read(railKey("project", projectId)) : false
}

export function writeProjectChatRailOpen(
	projectId: string,
	open: boolean,
): void {
	write(railKey("project", projectId), open)
}

/**
 * Same-tab signal asking the currently-mounted detail page to open its chat
 * rail now. Needed because navigating to the page you're already on is a
 * react-router no-op, so a localStorage write alone won't reopen the rail.
 */
export const OPEN_CHAT_RAIL_EVENT = "zplanem:open-chat-rail"

export interface OpenChatRailDetail {
	propertyId: string
	projectId: string | null
}

/**
 * Ask for a mention's chat rail to open: persist the preference (so a
 * navigation to a *different* page opens it on mount) AND fire a same-tab event
 * (so the page you're already on opens it immediately). Project mentions target
 * the project rail; otherwise the property rail.
 */
export function requestOpenChatRail(
	propertyId: string,
	projectId: string | null,
): void {
	if (projectId) writeProjectChatRailOpen(projectId, true)
	else writePropertyChatRailOpen(propertyId, true)
	if (typeof window !== "undefined") {
		window.dispatchEvent(
			new CustomEvent<OpenChatRailDetail>(OPEN_CHAT_RAIL_EVENT, {
				detail: { propertyId, projectId },
			}),
		)
	}
}
